import { cn } from '@/lib/utils'

export type ComposerAppearance = 'internal' | 'external'

interface ComposerTriggerState {
  isOpen?: boolean
  isSelected?: boolean
}

/**
 * Shared trigger chrome for composer doors (Effort, Workflows, Commands,
 * and the thread scope chip).
 *
 * `internal` is the home-pill scale used inside the composer box.
 * `external` is the quieter `text-xs` / muted scale used under the box.
 * Default at every mount is `external` so thread tests stay honest.
 */
export function composerTriggerClassName(
  appearance: ComposerAppearance = 'external',
  state: ComposerTriggerState = {},
): string {
  const isOpen = Boolean(state.isOpen)
  const isSelected = Boolean(state.isSelected)

  if (appearance === 'internal') {
    return cn(
      'inline-flex items-center rounded-full text-[13px]',
      'text-[var(--home-pill-inactive-fg)] transition-colors duration-200',
      'hover:bg-[var(--home-pill-track-bg)] hover:text-[var(--home-pill-inactive-fg-hover)]',
      'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
      (isOpen || isSelected) && 'bg-[var(--home-pill-track-bg)]',
      /* Home Lab footer: square icon tap targets on a phone, labelled chips from md.
         `overflow-hidden` + `min-w-8` so a still-visible caption cannot inflate
         the flex item and shove a neighbour off the row. */
      'h-7 w-7 min-w-7 shrink-0 justify-center gap-0 overflow-hidden px-0',
      'md:h-auto md:w-auto md:min-w-0 md:justify-start md:gap-1.5 md:overflow-visible md:px-3 md:py-1.5',
    )
  }

  return cn(
    'inline-flex items-center rounded-full text-xs transition-colors',
    'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
    (isOpen || isSelected) && 'bg-muted/70 text-foreground',
    /* Mobile thread row: one icon per door so Attach / Commands / Medium /
       Workflows cannot overlap. Desktop keeps the labelled chip. */
    'h-7 w-7 min-w-7 shrink-0 justify-center gap-0 overflow-hidden px-0',
    'md:h-auto md:w-auto md:min-w-0 md:justify-start md:gap-1.5 md:overflow-visible md:px-2.5 md:py-1',
  )
}

/** Hide the trigger word on small screens; `aria-label` still names the door.
 *  `hidden` not `sr-only`: screen-reader-only text is still in-flow for flex
 *  min-width, and it painted over neighbouring icons on the home composer. */
export function composerTriggerCaptionClassName(
  _appearance: ComposerAppearance = 'external',
): string {
  return 'hidden md:inline'
}

/** Chevrons are noise next to a lone icon; keep them from `md` up. */
export function composerTriggerChevronClassName(
  _appearance: ComposerAppearance = 'external',
): string {
  return 'hidden md:block'
}

/**
 * Shared panel chrome for the same doors. Placement and align stay on the
 * caller — this only swaps tokens.
 *
 * The fill is always the opaque popover token (`floating-surface` +
 * `bg-popover`). Composer shell tokens stay on the card; a menu that
 * hangs over page content cannot borrow a surface that may be unset or
 * match the page.
 */
export function composerPanelClassName(
  appearance: ComposerAppearance = 'external',
): string {
  const fill = 'floating-surface isolate bg-popover text-popover-foreground'

  if (appearance === 'internal') {
    return cn(
      fill,
      'border border-[var(--home-lab-composer-border)]',
      'shadow-[var(--home-card-shadow-active)]',
    )
  }

  return cn(fill, 'border border-border shadow-md')
}

/** Shared shell and circular action used by compact assistant and study composers. */
export const COMPOSER_SURFACE_CLASS = 'relative overflow-visible rounded-2xl bg-background dark:bg-twin-chat-surface transition-colors duration-150'

export function composerFlatSurfaceClassName(focused = false): string {
  return cn('border shadow-none', focused ? 'border-border' : 'border-border/60')
}

export const COMPOSER_ROUND_ACTION_CLASS = "relative flex shrink-0 items-center justify-center rounded-full after:absolute after:-inset-1 after:content-['']"
